import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Visibility,
  Download,
  BarChart,
  School,
  Person
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';

const Notes = () => {
  const { isAdmin, isEnseignant, isEtudiant, isChefFiliere, isDirectionGenerale, isCoordinateurPedagogique, user } = usePermissions();
  // La scolarite ne saisi pas : la direction, les enseignants et le chef de
  // filiere gerent les notes (saisie, controles, statistiques).
  const peutSaisirNotes = isAdmin || isEnseignant || isChefFiliere;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // États pour les données
  const [matieres, setMatieres] = useState([]);
  const [controles, setControles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [etudiants, setEtudiants] = useState([]);
  const [selectedMatiere, setSelectedMatiere] = useState('');
  const [selectedEtudiant, setSelectedEtudiant] = useState('');
  const [bulletin, setBulletin] = useState(null);
  
  // États pour les modals
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [formData, setFormData] = useState({
    etudiant_id: '',
    controle_id: '',
    matiere_id: '',
    valeur: '',
    commentaire: ''
  });
  const [selectedNote, setSelectedNote] = useState(null);
  
  // États pour les statistiques
  const [stats, setStats] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');

      // Charger les matières (filtrées par enseignant si rôle ENSEIGNANT)
      const matieresUrl = isEnseignant && userData.enseignant_id
        ? `/api/matieres/enseignant/${userData.enseignant_id}`
        : '/api/matieres';
      const matieresRes = await axios.get(matieresUrl, { headers });
      setMatieres(matieresRes.data || []);

      let etudiantsList = [];
      if (isAdmin || isChefFiliere || isDirectionGenerale || isCoordinateurPedagogique) {
        const etudiantsRes = await axios.get('/api/etudiants', { headers });
        etudiantsList = etudiantsRes.data?.data || [];
      } else if (isEnseignant && userData.enseignant_id) {
        // Charger uniquement les étudiants des groupes (filières/niveaux) de l'enseignant
        const mesMatieres = matieresRes.data || [];
        const groupKeys = mesMatieres
          .filter(m => m.filiere_id && m.niveau_id)
          .map(m => `${m.filiere_id}|${m.niveau_id}`);
        const groupesUniques = [...new Set(groupKeys)];

        let inscriptions = [];
        try {
          const insRes = await axios.get('/api/inscriptions', { headers, timeout: 8000 });
          inscriptions = insRes.data?.data || insRes.data || [];
        } catch (err) {
          console.error('Erreur chargement inscriptions:', err);
        }

        const idsEtudiants = new Set();
        inscriptions.forEach(ins => {
          const filiereId = ins.filiere?._id || ins.filiere_id;
          const niveauId = ins.niveau?._id || ins.niveau_id;
          if (
            ins.statut === 'CONFIRMEE' &&
            groupesUniques.includes(`${filiereId}|${niveauId}`)
          ) {
            const etudiantId = ins.etudiant?._id || ins.etudiant_id;
            if (etudiantId) idsEtudiants.add(String(etudiantId));
          }
        });

        if (idsEtudiants.size > 0) {
          const etudiantsRes = await axios.get('/api/etudiants', { headers });
          const all = etudiantsRes.data?.data || [];
          etudiantsList = all.filter(e => idsEtudiants.has(String(e._id)));
        }
      }

      // Si étudiant, charger ses notes
      if (isEtudiant) {
        const etudiant = etudiantsList.find(e => e.etudiant_email === userData.email) ||
          (etudiantsList.length === 0 ? null : etudiantsList[0]);
        if (etudiant) {
          setSelectedEtudiant(etudiant.etudiant_id || etudiant._id);
          await loadBulletin(etudiant.etudiant_id || etudiant._id);
        }
      }

      setEtudiants(etudiantsList);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const loadBulletin = async (etudiantId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/notes/etudiant/${etudiantId}/bulletin`, { headers });
      setBulletin(res.data);
      setSelectedEtudiant(etudiantId);
    } catch (err) {
      console.error('Erreur chargement bulletin:', err);
      setError('Impossible de charger le bulletin');
    }
  };

  const loadNotesByMatiere = async (matiereId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/notes/matiere/${matiereId}`, { headers });
      setNotes(res.data || []);
      setSelectedMatiere(matiereId);
    } catch (err) {
      console.error('Erreur chargement notes:', err);
    }
  };

  const loadControlesByMatiere = async (matiereId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/notes/controles/matiere/${matiereId}`, { headers });
      setControles(res.data || []);
    } catch (err) {
      console.error('Erreur chargement contrôles:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1 && selectedMatiere) {
      loadNotesByMatiere(selectedMatiere);
    }
    if (newValue === 2 && selectedEtudiant) {
      loadBulletin(selectedEtudiant);
    }
  };

  const handleMatiereChange = (matiereId) => {
    setSelectedMatiere(matiereId);
    loadControlesByMatiere(matiereId);
    loadNotesByMatiere(matiereId);
  };

  const handleAddNote = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post('/api/notes', formData, { headers });
      setSuccessMessage('Note ajoutée avec succès');
      handleCloseModal();
      loadNotesByMatiere(selectedMatiere);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'ajout');
    }
  };

  const handleUpdateNote = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`/api/notes/${selectedNote._id}`, formData, { headers });
      setSuccessMessage('Note modifiée avec succès');
      handleCloseModal();
      loadNotesByMatiere(selectedMatiere);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`/api/notes/${noteId}`, { headers });
      setSuccessMessage('Note supprimée avec succès');
      loadNotesByMatiere(selectedMatiere);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleOpenModal = (mode, note = null) => {
    setModalMode(mode);
    if (note) {
      setSelectedNote(note);
      setFormData({
        etudiant_id: note.etudiant_id || '',
        controle_id: note.controle_id || '',
        matiere_id: note.matiere_id || '',
        valeur: note.valeur || '',
        commentaire: note.commentaire || ''
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedNote(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      etudiant_id: '',
      controle_id: '',
      matiere_id: '',
      valeur: '',
      commentaire: ''
    });
  };

  const getMention = (moyenne) => {
    if (moyenne >= 18) return { label: 'Excellent', color: '#8b5cf6' };
    if (moyenne >= 16) return { label: 'Très Bien', color: '#3b82f6' };
    if (moyenne >= 14) return { label: 'Bien', color: '#10b981' };
    if (moyenne >= 12) return { label: 'Assez Bien', color: '#f59e0b' };
    if (moyenne >= 10) return { label: 'Passable', color: '#f97316' };
    return { label: 'Non validé', color: '#ef4444' };
  };

  const getTypeColor = (type) => {
    const colors = {
      'EXAMEN_FINAL': '#ef4444',
      'EXAMEN_INTERMEDIAIRE': '#f59e0b',
      'CONTROLE_CONTINU': '#10b981',
      'TP': '#3b82f6',
      'TD': '#8b5cf6',
      'PROJET': '#06b6d4',
      'STAGE': '#14b8a6'
    };
    return colors[type] || '#6b7280';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            📊 Gestion des Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEtudiant ? 'Mes notes et mon bulletin' : isEnseignant ? 'Saisie et gestion des notes' : 'Administration des notes'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          {peutSaisirNotes && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal('add')}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              Nouvelle Note
            </Button>
          )}
        </Stack>
      </Box>

      {/* Onglets */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab label="📋 Bulletin" />
          {peutSaisirNotes && <Tab label="📝 Saisie des notes" />}
          {peutSaisirNotes && <Tab label="📊 Statistiques" />}
        </Tabs>
      </Paper>

      {/* ========== TAB 1 : BULLETIN ========== */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
            👨‍🎓 Bulletin de notes
          </Typography>

          {/* Sélection de l'étudiant (navigation : aussi pour la direction générale et le coordinateur pédagogique) */}
          {(peutSaisirNotes || isDirectionGenerale || isCoordinateurPedagogique) && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Étudiant</InputLabel>
              <Select
                value={selectedEtudiant}
                onChange={(e) => loadBulletin(e.target.value)}
                label="Étudiant"
              >
                <MenuItem value="">Sélectionner un étudiant</MenuItem>
                {etudiants.map((e) => (
                  <MenuItem key={e.etudiant_id} value={e.etudiant_id}>
                    {e.etudiant_nom} {e.etudiant_prenom} ({e.etudiant_cne_masar})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Affichage du bulletin */}
          {bulletin ? (
            <Box>
              {/* En-tête du bulletin */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Étudiant :</strong> {bulletin.etudiant?.etudiant_nom} {bulletin.etudiant?.etudiant_prenom}</Typography>
                    <Typography variant="body2"><strong>CNE :</strong> {bulletin.etudiant?.etudiant_cne_masar}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Moyenne Générale :</strong> <strong style={{ color: '#667eea', fontSize: '1.2rem' }}>{bulletin.moyenneGenerale}/20</strong></Typography>
                    <Chip
                      label={getMention(parseFloat(bulletin.moyenneGenerale)).label}
                      sx={{ bgcolor: getMention(parseFloat(bulletin.moyenneGenerale)).color, color: 'white', mt: 1 }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Tableau des résultats */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Matière</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Crédits</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Moyenne</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Mention</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulletin.resultats?.map((r, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{r.matiere}</TableCell>
                        <TableCell>
                          <Chip label={r.code} size="small" sx={{ fontFamily: 'monospace' }} />
                        </TableCell>
                        <TableCell>{r.credits || '-'}</TableCell>
                        <TableCell>
                          <Typography fontWeight="bold" color={r.estValide ? '#10b981' : '#ef4444'}>
                            {r.moyenne}/20
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {r.estValide && <Chip label={r.mention} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af' }} />}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={r.estValide ? '✅ Validé' : '❌ Non validé'}
                            size="small"
                            sx={{ bgcolor: r.estValide ? '#dcfce7' : '#fee2e2', color: r.estValide ? '#166534' : '#991b1b' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Résumé */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Total crédits :</strong> {bulletin.totalCredits || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Crédits validés :</strong> {bulletin.creditsValides || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Mention :</strong> <Chip label={bulletin.mention} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af' }} /></Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography color="text.secondary">
                {isEtudiant ? 'Chargement de vos notes...' : 'Sélectionnez un étudiant pour voir son bulletin'}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* ========== TAB 2 : SAISIE DES NOTES ========== */}
      {tabValue === 1 && peutSaisirNotes && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
            📝 Saisie des notes
          </Typography>

          {/* Sélection de la matière */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Matière</InputLabel>
            <Select
              value={selectedMatiere}
              onChange={(e) => handleMatiereChange(e.target.value)}
              label="Matière"
            >
              <MenuItem value="">Sélectionner une matière</MenuItem>
              {matieres.map((m) => (
                <MenuItem key={m._id} value={m._id}>
                  {m.code} - {m.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedMatiere && (
            <>
              {/* Liste des notes */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Étudiant</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>CNE</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Contrôle</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Note</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notes.map((note) => (
                      <TableRow key={note._id} hover>
                        <TableCell>{note.etudiant_nom} {note.etudiant_prenom}</TableCell>
                        <TableCell>{note.etudiant_cne}</TableCell>
                        <TableCell>
                          <Chip
                            label={note.controle_intitule || 'Contrôle'}
                            size="small"
                            sx={{ bgcolor: getTypeColor(note.controle_type), color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="bold" color={note.valeur >= 10 ? '#10b981' : '#ef4444'}>
                            {note.valeur}/20
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={note.valeur >= 10 ? '✅ Validé' : '❌ Non validé'}
                            size="small"
                            sx={{ bgcolor: note.valeur >= 10 ? '#dcfce7' : '#fee2e2', color: note.valeur >= 10 ? '#166534' : '#991b1b' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Modifier">
                              <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', note)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              <IconButton size="small" color="error" onClick={() => handleDeleteNote(note._id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {notes.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                          <Typography color="text.secondary">Aucune note saisie pour cette matière</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {/* ========== TAB 3 : STATISTIQUES ========== */}
      {tabValue === 2 && peutSaisirNotes && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
            📊 Statistiques des notes
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Total des notes</Typography>
                  <Typography variant="h3" fontWeight="bold">0</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Taux de réussite</Typography>
                  <Typography variant="h3" fontWeight="bold">0%</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Moyenne générale</Typography>
                  <Typography variant="h3" fontWeight="bold">0/20</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, textAlign: 'center', py: 5, bgcolor: '#f8fafc', borderRadius: 2 }}>
            <Typography color="text.secondary">
              Sélectionnez une matière dans l'onglet "Saisie des notes" pour voir les statistiques
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ========== MODAL AJOUT/MODIFICATION NOTE ========== */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? '➕ Ajouter une note' : '✏️ Modifier la note'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Étudiant *</InputLabel>
              <Select
                value={formData.etudiant_id}
                onChange={(e) => setFormData({ ...formData, etudiant_id: e.target.value })}
                label="Étudiant *"
                required
              >
                <MenuItem value="">Sélectionner</MenuItem>
                {etudiants.map((e) => (
                  <MenuItem key={e.etudiant_id} value={e.etudiant_id}>
                    {e.etudiant_nom} {e.etudiant_prenom} ({e.etudiant_cne_masar})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Matière *</InputLabel>
              <Select
                value={formData.matiere_id}
                onChange={(e) => setFormData({ ...formData, matiere_id: e.target.value })}
                label="Matière *"
                required
              >
                <MenuItem value="">Sélectionner</MenuItem>
                {matieres.map((m) => (
                  <MenuItem key={m._id} value={m._id}>
                    {m.code} - {m.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Contrôle *</InputLabel>
              <Select
                value={formData.controle_id}
                onChange={(e) => setFormData({ ...formData, controle_id: e.target.value })}
                label="Contrôle *"
                required
              >
                <MenuItem value="">Sélectionner</MenuItem>
                {controles.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.intitule} (Coeff: {c.coefficient})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Note /20 *"
              type="number"
              inputProps={{ min: 0, max: 20, step: 0.25 }}
              value={formData.valeur}
              onChange={(e) => setFormData({ ...formData, valeur: parseFloat(e.target.value) || 0 })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Commentaire"
              value={formData.commentaire}
              onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
              margin="normal"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Annuler</Button>
          <Button
            onClick={modalMode === 'add' ? handleAddNote : handleUpdateNote}
            variant="contained"
            sx={{ bgcolor: '#667eea' }}
          >
            {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notes;


